import { useContext, useState, useEffect } from "react";
import { AppContext } from "../App";
import { FiUsers, FiAward, FiArrowRight, FiCheckCircle, FiInbox, FiBriefcase, FiMapPin, FiClock, FiFileText } from "react-icons/fi";
import bannerImg from "../assets/banner1.jpg";
import { callApi } from "../Helpers";

export default function CareerContent() {
  const appContext = useContext(AppContext);
  
  // State for jobs
  const [allJobs, setAllJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch jobs on component mount
  useEffect(() => {
    getAllJobs();
  }, []);

  function view(uuid) {
    appContext.navTo({
      item: 'side-view',
      subItem: 'job-options', 
      extraItem: uuid,
    })
  }

  // Trigger your applications drawer view 
  function openMyApplications() {
    appContext.navTo({
      item: 'side-view',
      subItem: 'my-applications'
    });
  }

  async function getAllJobs() {
    setIsLoading(true);
    return new Promise(async resolve => {
      await callApi('jobapplicant/get-open-jobs', {}).then(response => {
        if (response && response.status === 1) {
          setAllJobs(response.data);
          resolve(response.data);
        } else {
          setAllJobs([]);
          resolve(null);
        }
        setIsLoading(false);
      }).catch((err) => {
        setAllJobs([]);
        setIsLoading(false);
        resolve(null);
      });
    });
  }

  const benefits = [
    { 
      icon: <FiUsers />, 
      title: "Collaborative Culture", 
      desc: "Work alongside top-tier medical professionals in a multidisciplinary environment." 
    },
    { 
      icon: <FiAward />, 
      title: "Clinical Excellence", 
      desc: "Access to modern diagnostic tools and continuous professional development." 
    },
    { 
      icon: <FiCheckCircle />, 
      title: "Impactful Work", 
      desc: "Join a network dedicated to transforming healthcare standards in Dodoma." 
    },
  ];

  return (
    <>
      <style>{`
        :root {
          --clinical-teal: #0d9488;
          --navy-900: #0f172a;
          --slate-50: #f8fafc;
          --slate-100: #f1f5f9;
          --slate-200: #e2e8f0;
          --slate-500: #64748b;
          --font-main: 'Inter', system-ui, -apple-system, sans-serif;
        }

        .career-hero {
          position: relative;
          min-height: 60vh;
          display: flex;
          align-items: center;
          background: #000;
          overflow: hidden;
          font-family: var(--font-main);
        }

        .career-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.5;
        }

        .career-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(105deg, var(--navy-900) 0%, rgba(15, 23, 42, 0.8) 100%);
          z-index: 1;
        }

        .career-hero-content {
          position: relative;
          z-index: 2;
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 40px;
          width: 100%;
        }

        .career-glass-panel {
          max-width: 750px;
          padding: 60px;
          backdrop-filter: blur(8px);
          background: rgba(255, 255, 255, 0.03);
          border-left: 5px solid var(--clinical-teal);
        }

        .career-label {
          color: var(--clinical-teal);
          text-transform: uppercase;
          font-weight: 800;
          font-size: 0.8rem;
          letter-spacing: 2px;
          margin-bottom: 20px;
          display: block;
        }

        .career-title {
          color: #ffffff;
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: -0.04em;
          margin-bottom: 20px;
        }

        .career-section {
          padding: 100px 0;
          background: #ffffff;
          font-family: var(--font-main);
        }

        .benefit-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          margin-top: -100px;
          position: relative;
          z-index: 5;
          max-width: 1300px;
          margin-left: auto;
          margin-right: auto;
          padding: 0 24px;
        }

        .benefit-card {
          background: #ffffff;
          padding: 40px;
          border-radius: 12px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.05);
          border: 1px solid var(--slate-200);
        }

        /* ── Header Title Row Action ── */
        .section-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          border-bottom: 1px solid var(--slate-200);
          padding-bottom: 20px;
          margin-bottom: 40px;
        }

        .track-history-btn {
          background: transparent;
          color: var(--clinical-teal);
          border: 1px solid var(--clinical-teal);
          padding: 10px 20px;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .track-history-btn:hover {
          background: rgba(13, 148, 136, 0.05);
          transform: translateY(-1px);
        }

        /* ── Job Listing Styles ── */
        .jobs-container {
          max-width: 900px;
          margin: 80px auto 0;
          padding: 0 24px;
        }

        .job-card {
          background: var(--slate-50);
          border: 1px solid var(--slate-200);
          border-radius: 16px;
          padding: 30px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.3s ease;
        }

        .job-card:hover {
          border-color: var(--clinical-teal);
          background: #ffffff;
          box-shadow: 0 15px 30px rgba(0,0,0,0.06);
          transform: translateY(-3px);
        }

        .job-meta-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: var(--slate-200);
          color: var(--slate-500);
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          margin-right: 12px;
          margin-top: 12px;
        }

        .apply-btn {
          background: var(--navy-900);
          color: white;
          padding: 12px 24px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s ease;
          white-space: nowrap;
          cursor: pointer;
          border: none;
        }

        .apply-btn:hover {
          background: var(--clinical-teal);
          color: white;
        }

        /* ── Empty State ── */
        .empty-state-card {
          max-width: 800px;
          margin: 20px auto 0;
          padding: 80px 40px;
          text-align: center;
          background: var(--slate-50);
          border: 2px dashed var(--slate-200);
          border-radius: 24px;
        }

        .empty-icon-wrapper {
          width: 80px;
          height: 80px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          margin: 0 auto 30px;
          color: var(--slate-500);
          box-shadow: 0 10px 20px rgba(0,0,0,0.03);
        }

        @media (max-width: 992px) {
          .benefit-grid { grid-template-columns: 1fr; margin-top: 40px; }
          .career-hero { min-height: auto; padding: 80px 0; }
          .career-glass-panel { padding: 30px; margin: 0 20px; }
          .job-card { flex-direction: column; align-items: flex-start; gap: 20px; }
          .apply-btn { width: 100%; justify-content: center; }
          .section-header-row { flex-direction: column; align-items: center; gap: 16px; text-align: center; }
        }
      `}</style>

      {/* Hero Header */}
      <section className="career-hero">
        <img src={bannerImg} className="career-bg" alt="Clinical Professionals" />
        <div className="career-overlay" />
        <div className="career-hero-content">
           <div className="career-glass-panel">
            <span className="career-label">Join Our Medical Network</span>
            <h1 className="career-title">Advancing Healthcare <br/>Through Talent.</h1>
            <p className="text-white opacity-75 mb-0" style={{ maxWidth: '500px', lineHeight: '1.7' }}>
              We are seeking dedicated professionals to join the Nanguba Healthcare Network. 
              Discover a career where clinical rigor meets compassionate service.
            </p>
          </div>
        </div>
      </section>

      {/* Value Tiers */}
      <section className="career-section">
        <div className="benefit-grid pt-5">
          {benefits.map((b, i) => (
            <div key={i} className="benefit-card">
              <div className="benefit-icon" style={{ fontSize: '2rem', color: 'var(--clinical-teal)', marginBottom: '20px' }}>
                {b.icon}
              </div>
              <h5 className="fw-bold" style={{ color: 'var(--navy-900)' }}>{b.title}</h5>
              <p className="text-muted small mb-0">{b.desc}</p>
            </div>
          ))}
        </div>

        {/* Dynamic Jobs / Empty State Container */}
        <div className="jobs-container">
          
          {/* Section Header Row */}
          <div className="section-header-row">
            <div className="text-center">
              <h2 style={{ color: 'var(--navy-900)', fontWeight: 800, marginBottom: '15px' }}>Current Openings</h2>
              <div className="m-auto m-md-0" style={{ width: '40px', height: '4px', background: 'var(--clinical-teal)', }}></div>
            </div>
            
            {/* Elegant contextual history button */}
            <button className="track-history-btn" onClick={openMyApplications}>
              <FiFileText size={16} /> My Applications
            </button>
          </div>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--slate-500)' }}>
              Loading open positions...
            </div>
          ) : allJobs && allJobs.length > 0 ? (
            
            // Map through the retrieved jobs
            allJobs.map((job) => (
              <div key={job.id} className="job-card">
                <div>
                  <h4 style={{ color: 'var(--navy-900)', fontWeight: 800, margin: '0 0 8px 0' }}>
                    {job.title}
                  </h4>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {job.department && (
                      <span className="job-meta-chip"><FiBriefcase /> {job.department}</span>
                    )}
                    {job.location && (
                      <span className="job-meta-chip"><FiMapPin /> {job.location}</span>
                    )}
                    <span className="job-meta-chip"><FiClock /> {job.status || "Full-Time"}</span>
                  </div>
                </div>
                
                <button 
                  className="apply-btn btn-sm" 
                  onClick={() => view(job.uuid)}
                >
                  View Details <FiArrowRight />
                </button>
              </div>
            ))

          ) : (
            
            // Existing Empty State Component
            <div className="empty-state-card">
              <div className="empty-icon-wrapper">
                <FiInbox size={32} />
              </div>
              
              <h2 className="fw-extrabold mb-3" style={{ color: 'var(--navy-900)', fontSize: '2rem', letterSpacing: '-0.02em' }}>
                No Active Openings
              </h2>
              
              <p className="text-muted mx-auto mb-4" style={{ maxWidth: '500px', fontSize: '1.1rem', lineHeight: '1.6' }}>
                We don't have open matching vacancies at the moment. You can still look over your historical positions or join our talent registry pool.
              </p>

              {/* Added a secondary link row directly inside empty state */}
              <div className="mb-5">
                <button 
                  onClick={openMyApplications}
                  className="btn btn-link text-decoration-none fw-bold small" 
                  style={{ color: 'var(--clinical-teal)' }}
                >
                  Already applied? View submission statuses <FiArrowRight size={14} />
                </button>
              </div>

              <div className="pt-4 border-top">
                <h6 className="text-uppercase fw-bold mb-3" style={{ color: 'var(--clinical-teal)', letterSpacing: '1px', fontSize: '0.8rem' }}>
                  Register for Future Openings
                </h6>
                <p className="mb-4 small text-muted">Send your CV and a brief introduction to our HR department.</p>
                <a 
                  href="mailto:careers@nangubahealth.com" 
                  className="d-inline-flex align-items-center gap-2 text-decoration-none fw-bold p-3 px-4 rounded-pill" 
                  style={{ background: 'var(--navy-900)', color: 'white' }}
                >
                  careers@nangubahealth.com <FiArrowRight />
                </a>
              </div>
            </div>
            
          )}
        </div>
      </section>
    </>
  );
}